import {
  format,
  getBundleProductDetails,
  getIsAntiWaste,
} from '@monofront/business/utils';
import { useProduct } from '../ProductCard';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useProductPricing } from '@monofront/business/hooks/product/useProductPricing';
import QuestionMarkOutline from '../../../visuals/Icons/QuestionMarkOutline';
import { IconSizes } from '../../../visuals/Icons/type';
import { FC } from 'react';

type ProductPriceProps = {
  onTooltipClick?: () => void;
  quantity?: number;
};

export const ProductPrice: React.FC<ProductPriceProps> = ({
  onTooltipClick,
  quantity = 1,
}) => {
  const product = useProduct();
  const tCatalog = useTranslations('Catalog');

  const productPricing = product ? useProductPricing(product, quantity) : null;

  if (!product || !productPricing) {
    return (
      <div
        data-testid="product-price"
        className="bg-black-200 h-4 animate-pulse tw-w-1/2 tw-grid-area-[price]"
      ></div>
    );
  }

  const isAntiWaste = getIsAntiWaste(product.tags);
  const isComparisonPriceHidden = product.meta.finance?.hide_comparison_price;

  const { productBundleNumber, productBundleSale } = getBundleProductDetails(
    product.tags,
  );

  const isKit = Boolean(productBundleSale && product.combination_product_data);
  const isBundle = Boolean(!isKit && productBundleNumber && productBundleSale);

  return (
    <p
      data-testid="product-price"
      className="tw-inline-block tw-self-center tw-grid-area-[price]"
    >
      {!isComparisonPriceHidden && Boolean(productPricing.compareAtPrice) && (
        <s
          className={`
          tw-lf-discounted-price-sm tw-mr-1
          desktop:tw-lf-discounted-price-md
          `}
        >
          <span className="tw-sr-only">{tCatalog('oldPrice')}</span>
          {format(productPricing.compareAtPrice)}
        </s>
      )}
      {!isComparisonPriceHidden && productPricing.discountPercent && (
        <span className="tw-lf-discount tw-lf-discount-sm desktop:tw-lf-discount-md">
          {`-${productPricing.discountPercent}%`}
        </span>
      )}
      <span
        className={`tw-align-center tw-lf-title-sm tw-flex desktop:tw-lf-title-md ${
          isAntiWaste || isKit || isBundle ? 'tw-text-red-600' : ''
        }`}
      >
        {format(productPricing.price)}
        {onTooltipClick && (
          <button
            type="button"
            className="tw-lf-button-ghost tw-z-30 tw-ml-2 tw-w-4 tw-cursor-pointer tw-transition tw-duration-300 tw-ease-in-out hover:tw-opacity-60"
            onClick={onTooltipClick}
          >
            <span className="tooltip-icon tw-text-black-1000 [&_svg]:tw-align-top">
              <QuestionMarkOutline size={IconSizes.sm} aria-hidden />
            </span>
          </button>
        )}
      </span>
    </p>
  );
};

type HorizontalProductPriceProps = {
  quantity?: number;
};

export const HorizontalProductPrice: FC<HorizontalProductPriceProps> = ({
  quantity = 1,
}) => {
  const product = useProduct();
  const isComparisonPriceHidden = product?.meta.finance?.hide_comparison_price;

  const tCatalog = useTranslations('Catalog');
  const productPricing = product ? useProductPricing(product, quantity) : null;

  if (!productPricing || !product) {
    return (
      <div
        data-testid="product-price"
        className="bg-black-200 h-4 animate-pulse tw-w-1/2 tw-grid-area-[price]"
      ></div>
    );
  }

  const isAntiWaste = getIsAntiWaste(product.tags);

  const { productBundleNumber, productBundleSale } = getBundleProductDetails(
    product.tags,
  );

  const isKit = Boolean(productBundleSale && product.combination_product_data);
  const isBundle = Boolean(!isKit && productBundleNumber && productBundleSale);

  return (
    <p
      data-testid="product-price"
      className="productPrice !tw-mb-0 tw-max-h-11 tw-items-center tw-grid-area-[price] phone:tw-flex phone:tw-space-x-2"
    >
      <span
        className={`tw-lf-title-sm tw-block ${
          isAntiWaste || isKit || isBundle ? 'tw-text-red-600' : ''
        }`}
      >
        {format(productPricing.price)}
      </span>
      {!isComparisonPriceHidden && productPricing.discountPercent && (
        <span className="tw-lf-discount tw-lf-discount-sm tw-mr-1 desktop:tw-lf-discount-md md:tw-mr-0">
          {`-${productPricing.discountPercent}%`}
        </span>
      )}
      {!isComparisonPriceHidden && Boolean(productPricing.compareAtPrice) && (
        <span
          className={`
          tw-lf-discounted-price-sm
          `}
        >
          <span className="tw-sr-only">{tCatalog('oldPrice')}</span>
          {format(productPricing.compareAtPrice)}
        </span>
      )}
    </p>
  );
};

export const HiddenPrice: React.FC = () => {
  const tCatalog = useTranslations('Catalog');

  return (
    <Link href="/account/register/flow" legacyBehavior>
      <a
        data-testid="product-price-hidden"
        className="tw-lf-link-sm tw-inline-block tw-max-w-[100px] tw-justify-start tw-self-center tw-pr-2 tw-grid-area-[price] desktop:tw-lf-link-md"
        rel="nofollow"
      >
        {tCatalog('seeMemberPrice')}
      </a>
    </Link>
  );
};

export const HorizontalHiddenPrice: React.FC = () => {
  const tCatalog = useTranslations('Catalog');

  return (
    <Link href="/account/register/flow" legacyBehavior>
      <a
        data-testid="product-price-hidden"
        className="tw-lf-link-sm tw-self-center tw-grid-area-[price]"
        rel="nofollow"
      >
        {tCatalog('seeMemberPrice')}
      </a>
    </Link>
  );
};
